/**
 * deal-detail/render-info.ts — 헤더 / 상태 팝오버 / 기본정보 / 거래처·담당자 콤보 / 메모
 *
 * DealDetailView.ts 에서 분리 (정리 Step 3c, 2026-07-03 — 분해 계획 v2 §2).
 * 이동만 — 동작·마크업 변화 없음. 전부 "state → HTML 문자열" 순수 함수.
 * 이벤트는 data-action 배선이라 핸들러는 뷰 메서드 그대로.
 */

import * as api from '../../api/client';
import { escapeHtml, fmtDateTime } from '../../lib/cells';
import { statusBadgeStyle } from '../../lib/deal-status-color';
import { normalizePhone } from '../../lib/phone';
import {
  STATUS_ORDER, LEAD_SOURCE_OPTIONS, CLASSIFICATION_OPTIONS, DEAL_CANCEL_REASONS_MANUAL,
  type DealDetailData, type DealCancelModalState,
  type DealCustomerModalState, type DealContactModalState,
} from './types';

type ContextCollapsed = DealDetailData['contextCollapsed'];

/** 메타 select 옵션 렌더 (미지정 + 옵션들). 현재 값이 옵션에 없으면 미지정 처리(노출 X). */
function renderMetaOptions(options: readonly string[], current: string | null): string {
  const isValid = current != null && options.includes(current);
  const head = `<option value="" ${isValid ? '' : 'selected'}>미지정</option>`;
  return head + options.map((o) =>
    `<option value="${escapeHtml(o)}" ${o === current ? 'selected' : ''}>${escapeHtml(o)}</option>`,
  ).join('');
}

// ─── 헤더 (sticky) ──────────────────────────────────────────────────
export function renderHeader(
  full:              api.DealFull,
  newMode:           boolean,
  statusPopoverOpen: boolean,
  statusHistory:     api.DealStatusHistoryEntry[] | null,
): string {
  // 딜 생성 모드 — 블랭크 드래프트 위. 제목/액션 다르게(취소=삭제, 생성완료=상세 진입).
  // 채팅으로 딜명이 채워지면(임시값 '새 딜' 아님) 실제 딜명·딜코드를 헤더에 반영.
  if (newMode) {
    const named = !!full.name && full.name !== '새 딜';
    return `
      <header class="deal-detail-header">
        <div class="deal-detail-title">
          <h1>${named ? escapeHtml(full.name) : '신규 딜 생성'}</h1>
          <div class="deal-detail-subtitle text-muted text-sm">${named
            ? `<code>${escapeHtml(full.deal_no ?? `DEAL-${full.id}`)}</code> · 채팅으로 계속 편집하세요 (자동 저장)`
            : 'NEW DEAL · 우측 채팅으로 딜 생성 자료를 올리세요'}</div>
        </div>
        <div class="deal-detail-actions">
          <button class="btn btn-ghost btn-sm" type="button" data-action="new-cancel">취소</button>
        </div>
      </header>
    `;
  }
  return `
      <header class="deal-detail-header">
        <div class="deal-detail-title">
          <h1>${escapeHtml(full.name)}</h1>
          <div class="deal-detail-subtitle text-muted text-sm">
            <code>${escapeHtml(full.deal_no ?? `DEAL-${full.id}`)}</code>
            <button class="deal-copy-btn" type="button" data-action="copy-deal-link"
                    title="딜 링크 복사 (이름+URL 하이퍼링크)" aria-label="딜 링크 복사">⧉</button>
            <button class="deal-watch-star${full.is_watched ? ' is-on' : ''}" type="button"
                    data-action="watch" data-deal-id="${escapeHtml(full.id)}"
                    aria-label="관심" aria-pressed="${full.is_watched ? 'true' : 'false'}"
                    title="${full.is_watched ? '관심 해제' : '관심 등록'}">${full.is_watched ? '★' : '☆'}</button>
            · ${escapeHtml(fmtDateTime(full.created_at))} 등록
          </div>
        </div>
        <div class="deal-detail-actions">
          <button class="btn btn-ghost btn-sm" type="button" data-action="restart-config">구성 재시작</button>
          ${renderStatusTrigger(full, statusPopoverOpen, statusHistory)}
        </div>
      </header>
    `;
}

// ─── 헤더 — 현재 상태 트리거 + 히스토리 팝오버 (STATUS-001/002, §4.14) ─
function renderStatusTrigger(
  full:          api.DealFull,
  open:          boolean,
  statusHistory: api.DealStatusHistoryEntry[] | null,
): string {
  return `
      <div class="deal-status-control">
        <button class="deal-status-trigger" type="button"
                data-action="status-popover-toggle" aria-expanded="${open ? 'true' : 'false'}">
          <span class="deal-status-current-chip" style="${statusBadgeStyle(full.status)}">${escapeHtml(full.status)}</span>
          <span class="deal-status-caret">▾</span>
        </button>
        ${open ? `
          <div class="deal-status-popover-overlay" data-action="status-popover-close"></div>
          ${renderStatusPopover(full, statusHistory)}
        ` : ''}
      </div>
    `;
}

function renderStatusPopover(
  full: api.DealFull,
  hist: api.DealStatusHistoryEntry[] | null,
): string {
  const isCanceled = full.status === '취소';
  const fmtSlash = (iso: string) => {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
  };

  let historyHtml: string;
  if (hist === null) {
    historyHtml = '<li class="deal-status-hist is-future">불러오는 중…</li>';
  } else if (hist.length === 0) {
    historyHtml = '<li class="deal-status-hist is-future">기록 없음</li>';
  } else {
    // 실제 전이 이력 (반복/역행 포함). 마지막 = 현재 상태(강조).
    const lastIdx = hist.length - 1;
    historyHtml = hist.map((h, i) =>
      `<li class="deal-status-hist${i === lastIdx && !isCanceled ? ' is-current' : ''}">
           <span class="deal-status-hist-name">${escapeHtml(h.status)}</span>
           <span class="deal-status-hist-date">${escapeHtml(fmtSlash(h.changed_at))}</span>
         </li>`).join('');

    // 현재 상태 이후 단계들은 회색(미래)으로 표시 (§4.14.6-8). 취소면 미래 없음.
    if (!isCanceled) {
      const curIdx = STATUS_ORDER.indexOf(full.status as api.DealStatus);
      if (curIdx >= 0) {
        historyHtml += STATUS_ORDER.slice(curIdx + 1).map((s) =>
          `<li class="deal-status-hist is-future">
               <span class="deal-status-hist-name">${escapeHtml(s)}</span>
             </li>`).join('');
      }
    }
  }

  return `
      <div class="deal-status-popover" role="dialog" aria-label="딜 상태">
        ${isCanceled ? '<span class="deal-status-cancel-chip">취소됨</span>' : ''}
        <div class="deal-status-hist-label">전체 히스토리</div>
        <ol class="deal-status-history-list">${historyHtml}</ol>
      </div>
    `;
}

// ─── 좌측 — 기본 정보 ──────────────────────────────────────────────
export function renderInfoSection(
  full:      api.DealFull,
  assignees: api.UserAssignee[],
): string {
  return `
      <section class="deal-section">
        <h2 class="deal-section-title">기본 정보</h2>
        <dl class="deal-info-grid">
          <div class="deal-info-item">
            <dt>등록일</dt>
            <dd>${escapeHtml(fmtDateTime(full.created_at))}</dd>
          </div>

          <div class="deal-info-item">
            <dt>최근 수정일</dt>
            <dd>${escapeHtml(fmtDateTime(full.updated_at))}</dd>
          </div>

          <div class="deal-info-item">
            <dt>거래처</dt>
            <dd>${renderCustomerSummary(full)}</dd>
          </div>

          <div class="deal-info-item">
            <dt>문의분류</dt>
            <dd>
              <select class="deal-meta-select" data-action="classification-select">
                <option value="" ${full.classification == null ? 'selected' : ''}>선택 없음</option>
                ${CLASSIFICATION_OPTIONS.map((o) =>
                  `<option value="${escapeHtml(o)}" ${o === full.classification ? 'selected' : ''}>${escapeHtml(o)}</option>`,
                ).join('')}
              </select>
            </dd>
          </div>

          <div class="deal-info-item">
            <dt>거래처 담당자</dt>
            <dd>${renderContactSummary(full)}</dd>
          </div>

          <div class="deal-info-item">
            <dt>영업 담당자</dt>
            <dd>
              <select class="deal-assignee-select" data-action="assignee-select">
                <option value="" ${full.assignee_user_id == null ? 'selected' : ''}>미배정</option>
                ${assignees.map((a) => `
                  <option value="${escapeHtml(a.id)}" ${a.id === full.assignee_user_id ? 'selected' : ''}>
                    ${escapeHtml(a.name)}
                  </option>
                `).join('')}
              </select>
            </dd>
          </div>

          <div class="deal-info-item">
            <dt>유입 경로</dt>
            <dd>
              <select class="deal-meta-select" data-action="lead-source-select">
                ${renderMetaOptions(LEAD_SOURCE_OPTIONS, full.inquiry_channel ?? null)}
              </select>
            </dd>
          </div>

          <div class="deal-info-item">
            <dt>영업담당자 연락처</dt>
            <dd>
              ${(() => {
                const assignee = assignees.find((a) => a.id === full.assignee_user_id) ?? null;
                if (!assignee) return '<span class="deal-info-muted">영업담당자 지정 후 입력</span>';
                return `<div class="deal-inline-edit-row">
                          <input class="deal-assignee-phone-input" type="text" inputmode="tel"
                                 data-action="assignee-phone-save" placeholder="휴대폰 번호 (견적서에 사용)"
                                 value="${escapeHtml(assignee.phone ?? '')}" />
                          <button class="btn btn-secondary btn-sm" type="button" data-action="assignee-phone-save-btn">저장</button>
                        </div>`;
              })()}
            </dd>
          </div>
        </dl>
      </section>
    `;
}

// ─── 거래처 — 요약 + 변경(모달 트리거) — 2026-07-14 스펙 ──────────────
function renderCustomerSummary(full: api.DealFull): string {
  const name = full.customer_name?.trim();
  const bno  = full.customer_business_no?.trim();
  // draft(컨펌 대기)면 변경 버튼 숨김 — 컨펌 배너의 버튼으로만 처리(마이그139).
  const changeBtn = full.customer_is_draft
    ? ''
    : `<button class="btn btn-secondary btn-sm" type="button" data-action="customer-change-open">변경</button>`;
  // draft(Agent 생성·미확인) 거래처면 컨펌 배너 노출 — 마이그 139
  const banner = full.customer_is_draft
    ? `<div class="deal-draft-confirm">
         <span class="deal-draft-confirm-msg">Agent에 의해 신규 생성된 거래처입니다. 컨펌이 필요합니다.</span>
         <button class="btn btn-secondary btn-sm deal-draft-confirm-btn" type="button" data-action="customer-confirm-open">컨펌</button>
       </div>`
    : '';
  if (!name) {
    return `<div class="deal-party-summary"><span class="deal-info-muted">미지정</span>${changeBtn}</div>${banner}`;
  }
  return `<div class="deal-party-summary">
      <div class="deal-party-main">${escapeHtml(name)}${bno ? ` <small class="deal-party-sub">사업자 ${escapeHtml(bno)}</small>` : ''}</div>
      ${changeBtn}
    </div>${banner}`;
}

// ─── 거래처 선택 모달 (검색 + 담당자 수 + 신규 등록) ───────────────────
// 검색 결과만 별도(포커스 보존 — 입력 중 결과 영역만 targeted 갱신).
export function renderDealCustomerModalResults(s: DealCustomerModalState): string {
  const q = s.query.trim();
  if (s.searching) return `<div class="deal-party-modal-empty">검색 중…</div>`;
  if (s.hits.length === 0) return `<div class="deal-party-modal-empty">${q ? '검색 결과가 없습니다.' : '거래처명·대표자·사업자번호·주소로 검색하세요.'}</div>`;
  return `<div class="deal-party-modal-list">${s.hits.map((c) =>
    `<button type="button" class="deal-party-modal-row" data-action="customer-modal-pick"
             data-id="${escapeHtml(c.id)}" data-name="${escapeHtml(c.name)}" data-bno="${escapeHtml(c.business_no ?? '')}">
       <span class="deal-party-modal-name">${escapeHtml(c.name)}${c.business_no ? ` <small>(${escapeHtml(c.business_no)})</small>` : ''}</span>
       <span class="deal-party-modal-count">담당자 ${c.contact_count}</span>
     </button>`).join('')}</div>`;
}

export function renderDealCustomerModal(s: DealCustomerModalState): string {
  if (!s.open) return '';
  const results = renderDealCustomerModalResults(s);
  return `
    <div class="deal-party-modal-wrap">
      <div class="deal-party-modal-overlay" data-action="customer-modal-overlay"></div>
      <div class="deal-party-modal" role="dialog" aria-label="거래처 지정">
        <div class="deal-party-modal-head">
          <div>
            <h3>거래처 지정</h3>
            <p class="deal-party-modal-desc">기존 거래처를 검색하거나 새 거래처를 등록합니다. (검색 결과 더블클릭으로 지정)</p>
          </div>
        </div>
        <div class="deal-party-modal-body">
          <div class="deal-party-modal-seclabel">거래처 검색</div>
          <input class="input deal-party-modal-search" data-action="customer-modal-search" autocomplete="off"
                 placeholder="거래처명·대표자·사업자등록번호 검색" value="${escapeHtml(s.query)}" />
          <div class="deal-party-modal-scroll">${results}</div>
          <div class="deal-party-modal-newsec">
            <div class="deal-party-modal-newhead">
              <span class="deal-party-modal-seclabel">신규 거래처</span>
              <span class="deal-party-modal-newhint">거래처명만 먼저 등록할 수 있습니다.</span>
            </div>
            <label class="deal-party-modal-flabel">거래처명</label>
            <input class="input" data-action="customer-modal-newname" autocomplete="off" value="${escapeHtml(s.newName)}" placeholder="거래처명" />
            ${s.error ? `<div class="deal-contact-err">${escapeHtml(s.error)}</div>` : ''}
          </div>
        </div>
        <div class="deal-party-modal-foot">
          <button class="btn btn-primary btn-sm" type="button" data-action="customer-modal-create-save" ${s.saving ? 'disabled' : ''}>${s.saving ? '등록 중…' : '신규 거래처 등록'}</button>
          <button class="btn btn-secondary btn-sm" type="button" data-action="customer-modal-close">취소</button>
        </div>
      </div>
    </div>`;
}

// ─── 거래처 담당자 — 요약 + 변경(모달 트리거) ─────────────────────────
export function renderContactSummary(full: api.DealFull): string {
  // 담당자는 거래처에 속함 — 거래처 없으면 지정 불가
  if (full.customer_id == null) {
    return `<span class="deal-info-muted">거래처를 먼저 지정하세요</span>`;
  }
  const name = full.contact_name?.trim();
  const meta = [normalizePhone(full.contact_phone), full.contact_email].filter(Boolean).map((v) => escapeHtml(v as string)).join(' · ');
  // draft(컨펌 대기)면 변경 버튼 숨김 — 컨펌 배너의 버튼으로만 처리(마이그139).
  const changeBtn = full.contact_is_draft
    ? ''
    : `<button class="btn btn-secondary btn-sm" type="button" data-action="contact-change-open">변경</button>`;
  // draft(Agent 생성·미확인) 담당자면 컨펌 배너 노출 — 마이그 139
  const banner = full.contact_is_draft
    ? `<div class="deal-draft-confirm">
         <span class="deal-draft-confirm-msg">Agent에 의해 신규 생성된 담당자입니다. 컨펌이 필요합니다.</span>
         <button class="btn btn-secondary btn-sm deal-draft-confirm-btn" type="button" data-action="contact-confirm-open">컨펌</button>
       </div>`
    : '';
  if (!name) {
    // draft 인데 이름만 없고 연락처(전화·이메일)가 잡혀 있으면, '미지정' 대신 그 연락처를
    //   보여준다 — 채팅으로 받은 담당자 정보가 유실된 것처럼 보이지 않게(마이그 194).
    const body = (full.contact_is_draft && meta)
      ? `<div><div class="deal-party-main deal-info-muted">(성함 미확인)</div><div class="deal-party-meta"><small>${meta}</small></div></div>`
      : `<span class="deal-info-muted">담당자 미지정</span>`;
    return `<div class="deal-party-summary">${body}${changeBtn}</div>${banner}`;
  }
  return `<div class="deal-party-summary">
      <div>
        <div class="deal-party-main">${escapeHtml(name)}${full.contact_position ? ` <small class="deal-party-sub">${escapeHtml(full.contact_position)}</small>` : ''}</div>
        ${meta ? `<div class="deal-party-meta"><small>${meta}</small></div>` : ''}
      </div>
      ${changeBtn}
    </div>${banner}`;
}

// ─── 담당자 선택 모달 (회사 소속 검색 + 신규 등록) ─────────────────────
export function renderDealContactModalResults(s: DealContactModalState): string {
  const q = s.query.trim().toLowerCase();
  const qDigits = q.replace(/[^0-9]/g, '');
  const filtered = q
    ? s.contacts.filter((c) =>
        [c.name, c.position, c.email].some((v) => (v ?? '').toLowerCase().includes(q))
        || (qDigits !== '' && (c.phone ?? '').replace(/[^0-9]/g, '').includes(qDigits)))
    : s.contacts;
  if (s.loading) return `<div class="deal-party-modal-empty">불러오는 중…</div>`;
  if (filtered.length === 0) return `<div class="deal-party-modal-empty">${s.contacts.length === 0 ? '등록된 담당자가 없습니다. 신규로 등록하세요.' : '검색 결과가 없습니다.'}</div>`;
  return `<div class="deal-party-modal-list">${filtered.map((c) => {
    const meta = [c.position, normalizePhone(c.phone), c.email].filter(Boolean).map((v) => escapeHtml(v as string)).join(' · ');
    return `<button type="button" class="deal-party-modal-row" data-action="contact-modal-pick"
              data-id="${escapeHtml(c.id)}" data-name="${escapeHtml(c.name)}"
              data-phone="${escapeHtml(c.phone ?? '')}" data-email="${escapeHtml(c.email ?? '')}" data-position="${escapeHtml(c.position ?? '')}">
         <span class="deal-party-modal-name">${escapeHtml(c.name)}</span>
         ${meta ? `<span class="deal-party-modal-sub">${meta}</span>` : ''}
       </button>`;
  }).join('')}</div>`;
}

export function renderDealContactModal(s: DealContactModalState): string {
  if (!s.open) return '';
  const results = renderDealContactModalResults(s);
  const desc = s.companyName ? `${escapeHtml(s.companyName)} 소속 담당자를 검색해 선택합니다.` : '현재 거래처 소속 담당자를 검색해 선택합니다.';
  return `
    <div class="deal-party-modal-wrap">
      <div class="deal-party-modal-overlay" data-action="contact-modal-overlay"></div>
      <div class="deal-party-modal" role="dialog" aria-label="담당자 지정">
        <div class="deal-party-modal-head">
          <div>
            <h3>담당자 지정</h3>
            <p class="deal-party-modal-desc">${desc} (검색 결과 더블클릭으로 지정)</p>
          </div>
        </div>
        <div class="deal-party-modal-body">
          <div class="deal-party-modal-seclabel">담당자 검색</div>
          <input class="input deal-party-modal-search" data-action="contact-modal-search" autocomplete="off"
                 placeholder="이름·직책·이메일·전화번호 검색" value="${escapeHtml(s.query)}" />
          <div class="deal-party-modal-scroll">${results}</div>
          <div class="deal-party-modal-newsec">
            <div class="deal-party-modal-newhead">
              <span class="deal-party-modal-seclabel">신규 담당자</span>
              <span class="deal-party-modal-newhint">이름은 필수이며 연락처는 선택입니다.</span>
            </div>
            <div class="deal-party-modal-newgrid">
              <div>
                <label class="deal-party-modal-flabel">이름 <b style="color:#dc2626">*</b></label>
                <input class="input" data-action="contact-modal-newfield" data-field="newName" autocomplete="off" value="${escapeHtml(s.newName)}" placeholder="담당자명" />
              </div>
              <div>
                <label class="deal-party-modal-flabel">직책</label>
                <input class="input" data-action="contact-modal-newfield" data-field="newPosition" autocomplete="off" value="${escapeHtml(s.newPosition)}" placeholder="대리, 과장 등" />
              </div>
            </div>
            <div class="deal-party-modal-newgrid">
              <div>
                <label class="deal-party-modal-flabel">소속</label>
                <input class="input" data-action="contact-modal-newfield" data-field="newAffiliation" autocomplete="off" value="${escapeHtml(s.newAffiliation)}" placeholder="부서/팀 등" />
              </div>
              <div>
                <label class="deal-party-modal-flabel">이메일</label>
                <input class="input" data-action="contact-modal-newfield" data-field="newEmail" type="email" value="${escapeHtml(s.newEmail)}" placeholder="contact@example.com" />
              </div>
            </div>
            <div class="deal-party-modal-newgrid">
              <div>
                <label class="deal-party-modal-flabel">전화번호</label>
                <input class="input" data-action="contact-modal-newfield" data-field="newPhone" inputmode="tel" value="${escapeHtml(s.newPhone)}" placeholder="010-1234-5678" />
                <p class="deal-party-modal-fieldhint">하이픈 없이 입력해도 자동 변환돼요.</p>
              </div>
              <div>
                <label class="deal-party-modal-flabel">사무실번호</label>
                <input class="input" data-action="contact-modal-newfield" data-field="newOfficePhone" value="${escapeHtml(s.newOfficePhone)}" placeholder="02-1234-5678" />
                <p class="deal-party-modal-fieldhint">하이픈을 포함해 입력하세요.</p>
              </div>
            </div>
            <div class="deal-party-modal-newfull">
              <label class="deal-party-modal-flabel">메모</label>
              <textarea class="input" data-action="contact-modal-newfield" data-field="newNotes" rows="2" placeholder="메모">${escapeHtml(s.newNotes)}</textarea>
            </div>
            ${s.error ? `<div class="deal-contact-err">${escapeHtml(s.error)}</div>` : ''}
          </div>
        </div>
        <div class="deal-party-modal-foot">
          <button class="btn btn-primary btn-sm" type="button" data-action="contact-modal-create-save" ${s.saving ? 'disabled' : ''}>${s.saving ? '등록 중…' : '신규 담당자 등록'}</button>
          <button class="btn btn-secondary btn-sm" type="button" data-action="contact-modal-close">취소</button>
        </div>
      </div>
    </div>`;
}

// ─── 메모 — 기타 조건 + 원문 + 히스토리 (admin-ux-preview 기준) ──────
export function renderHistorySectionStub(
  full:       api.DealFull,
  collapsed:  ContextCollapsed,
  dealEvents: api.DealEvent[],
): string {
  const cond  = full.other_conditions?.trim();
  const orig  = full.original_text?.trim();
  const notes = full.notes?.trim();

  // 히스토리 = deal_event 활동 로그(감사 로그, 채팅과 분리). 최신순은 서버에서 정렬.
  const historyEntries = dealEvents;

  const foldBtn = (action: string, isCollapsed: boolean) => `
      <button class="deal-context-fold" type="button" data-action="${action}"
              aria-expanded="${!isCollapsed}">${isCollapsed ? '펼치기' : '접기'}</button>`;

  const condPanel = `
      <section class="deal-context-panel">
        <div class="deal-context-head"><h3 class="deal-context-title">기타 조건</h3></div>
        ${cond
          ? `<div class="deal-context-body"><pre class="deal-context-text">${escapeHtml(cond)}</pre></div>`
          : `<p class="empty-context">기록된 기타 조건이 없습니다.</p>`}
      </section>`;

  const notesPanel = notes ? `
      <section class="deal-context-panel">
        <div class="deal-context-head"><h3 class="deal-context-title">영업담당 메모</h3></div>
        <div class="deal-context-body"><pre class="deal-context-text">${escapeHtml(notes)}</pre></div>
      </section>` : '';

  const origPanel = `
      <section class="deal-context-panel">
        <div class="deal-context-head">
          <h3 class="deal-context-title">원문</h3>
          ${foldBtn('toggle-original', collapsed.original)}
        </div>
        <div class="deal-context-body${collapsed.original ? ' collapsed' : ''}">
          ${orig
            ? `<div class="deal-original-box"><pre class="deal-context-text">${escapeHtml(orig)}</pre></div>`
            : `<p class="empty-context">원문이 없습니다.</p>`}
        </div>
      </section>`;

  const histRows = historyEntries.length
    ? historyEntries.map((ev) => `
          <div class="deal-history-row">
            <div class="deal-history-time">${escapeHtml(fmtDateTime(ev.created_at))}</div>
            <div class="deal-history-text">${escapeHtml(ev.message)}</div>
          </div>`).join('')
    : `<p class="empty-context">기록된 히스토리가 없습니다.</p>`;

  const histPanel = `
      <section class="deal-context-panel">
        <div class="deal-context-head">
          <h3 class="deal-context-title">히스토리</h3>
          ${foldBtn('toggle-history', collapsed.history)}
        </div>
        <div class="deal-context-body${collapsed.history ? ' collapsed' : ''}">
          <div class="deal-history-list">${histRows}</div>
        </div>
      </section>`;

  // 딜 취소 — 메모 카드 맨 아래. 이미 취소면 사유 노출, 아니면 취소 버튼.
  const cancelFooter = full.status === '취소'
    ? `<div class="deal-cancel-footer">
         <span class="deal-cancel-note">이 딜은 취소되었습니다${full.cancel_reason ? ` — 사유: <strong>${escapeHtml(full.cancel_reason)}</strong>` : ''}.</span>
       </div>`
    : `<div class="deal-cancel-footer">
         <button type="button" class="deal-cancel-btn" data-action="cancel-deal-open">딜 취소</button>
       </div>`;

  return `
      <section class="deal-section">
        <h2 class="deal-section-title">메모</h2>
        <div class="deal-context-stack">
          ${condPanel}
          ${notesPanel}
          ${origPanel}
          ${histPanel}
        </div>
        ${cancelFooter}
      </section>
    `;
}

// ─── 딜 취소 모달 — 확인 + 사유(수동 5종) 필수 선택 ─────────────────
export function renderDealCancelModal(
  state:    DealCancelModalState,
  dealName: string,
): string {
  if (!state.open) return '';
  const canConfirm = !!state.reason && !state.saving;
  const reasonBtns = DEAL_CANCEL_REASONS_MANUAL.map((r) => `
          <button type="button"
                  class="deal-cancel-reason${state.reason === r ? ' is-selected' : ''}"
                  data-action="cancel-reason-pick" data-reason="${escapeHtml(r)}">${escapeHtml(r)}</button>`).join('');
  return `
      <div class="deal-rfq-modal" data-cancel-modal>
        <div class="deal-rfq-modal-overlay" data-action="cancel-deal-overlay"></div>
        <div class="deal-rfq-modal-dialog deal-cancel-dialog" role="dialog" aria-label="딜 취소">
          <header class="deal-rfq-modal-header">
            <h2>딜을 취소하시겠습니까?</h2>
            <button class="deal-rfq-modal-close" type="button" data-action="cancel-deal-close" aria-label="닫기">✕</button>
          </header>
          <div class="deal-rfq-modal-body">
            <p class="deal-cancel-desc">“${escapeHtml(dealName)}” 딜을 취소 처리합니다. 취소 사유를 선택하세요 <strong>(필수)</strong>.</p>
            <div class="deal-cancel-reasons">${reasonBtns}</div>
            ${state.error ? `<p class="deal-cancel-error">${escapeHtml(state.error)}</p>` : ''}
          </div>
          <footer class="deal-rfq-modal-footer">
            <button type="button" class="deal-cancel-cancel-btn" data-action="cancel-deal-close">닫기</button>
            <button type="button" class="deal-cancel-confirm-btn" data-action="cancel-deal-confirm" ${canConfirm ? '' : 'disabled'}>${state.saving ? '취소 중…' : '딜 취소'}</button>
          </footer>
        </div>
      </div>`;
}
